// src/components/home/VibeModulesPreview.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Star, Zap, Brain, Heart, Target, Crown } from 'lucide-react';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { useApp } from '../../context/AppContext';

const VibeModulesPreview = () => {
  const { setCurrentView } = useApp();
  
  // Vibe-themed preview modules
  const vibeModules = [
    {
      id: 1,
      icon: Brain,
      title: "Your Brain's Secret Soundtrack",
      subtitle: 'Become your own inner DJ',
      description: 'Discover the patterns your brain plays on repeat and learn to curate a soundtrack that actually serves you.',
      level: 'Foundation', 
      duration: '8 min',
      color: 'from-purple-500 to-indigo-500',
      vibeCheck: "🎧 What's your brain playing right now?",
      keySkills: ['Awareness building', 'Pattern recognition', 'Mental DJ skills'],
    },
    {
      id: 2,
      icon: Target,
      title: 'Thought Detective Mode',
      subtitle: 'Fact-check your brain like a pro',
      description: 'Learn to spot when your brain is creating fake news about your life and become a detective of your own thoughts.',
      level: 'Essential',
      duration: '10 min', 
      color: 'from-pink-500 to-purple-500',
      vibeCheck: '🕵️ Is this thought actually facts or fiction?',
      keySkills: ['Critical thinking', 'Evidence gathering', 'Reality testing'],
    },
    {
      id: 3,
      icon: Heart,
      title: 'Emotion Surfing Academy',
      subtitle: "Ride the waves, don't fight them",
      description: 'Master the art of riding emotional waves instead of being wiped out by them. Surfing > drowning every time.',
      level: 'Advanced',
      duration: '12 min',
      color: 'from-blue-500 to-teal-500', 
      vibeCheck: '🌊 Ready to surf instead of struggle?',
      keySkills: ['Emotional regulation', 'Mindful awareness', 'Self-compassion'],
    },
  ];
  
  return (
<section className="py-20 bg-gradient-to-br from-slate-800/30 to-purple-900/20 relative">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Crown className="w-6 h-6 text-yellow-400" />
            <span className="text-yellow-400 font-medium">Your Learning Journey</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Modules That Actually Hit Different
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            No boring textbook vibes here. These are bite-sized, science-backed modules designed to 
            <span className="text-purple-300 font-semibold"> actually transform how you think and feel about yourself.</span>
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {vibeModules.map((module, index) => (
            <motion.div
              key={module.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="h-full"
            >
              <Card className="p-6 h-full hover-lift bg-gradient-to-br from-white/5 to-white/10 border-purple-500/20 relative overflow-hidden group">
                {/* Background glow effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${module.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                
                <div className="relative space-y-4 h-full flex flex-col">
                  {/* Header */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                        <module.icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          module.level === 'Foundation' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                          module.level === 'Essential' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
                          'bg-red-500/20 text-red-300 border border-red-500/30'
                        }`}>
                          {module.level}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-white group-hover:text-purple-200 transition-colors">
                        {module.title}
                      </h3>
                      <p className="text-purple-200 font-medium">
                        {module.subtitle}
                      </p>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 space-y-4">
                    <p className="text-slate-300 leading-relaxed">
                      {module.description}
                    </p>
                    
                    {/* Vibe Check */}
                    <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                      <p className="text-purple-300 text-sm font-medium">
                        {module.vibeCheck}
                      </p>
                    </div>
                    
                    {/* Key Skills */}
                    <div>
                      <h4 className="text-white font-medium mb-2 flex items-center gap-1">
                        <Zap className="w-3 h-3 text-yellow-400" />
                        You'll Master:
                      </h4>
                      <div className="space-y-1">
                        {module.keySkills.map((skill, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                            <Star className="w-3 h-3 text-purple-400" />
                            <span>{skill}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div className="pt-4 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-400 text-sm">
                        <Clock className="w-3 h-3" />
                        <span>{module.duration} read</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-purple-300 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => setCurrentView('modules')}
                      >
                        Start Module
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
        
        {/* All Modules CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">8 Transformative Modules Waiting ✨</h3>
            <p className="text-slate-300 mb-6">
              From mastering your inner DJ to building unshakeable confidence, we've got your complete transformation journey mapped out.
            </p>
            <Button 
              onClick={() => setCurrentView('modules')} 
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              Explore All Modules
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </motion.div>
        
        {/* Progress Pathway */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1 }}
          className="mt-16 text-center"
        >
          <h4 className="text-lg font-semibold text-white mb-6">Your Transformation Pathway</h4>
          <div className="flex justify-center items-center gap-4 max-w-2xl mx-auto">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                <span className="text-green-300 font-bold text-sm">1</span>
              </div>
              <span className="text-green-300 text-sm font-medium">Foundation</span>
            </div>
            <div className="w-8 h-0.5 bg-slate-600"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <span className="text-yellow-300 font-bold text-sm">2</span>
              </div>
              <span className="text-yellow-300 text-sm font-medium">Skills</span>
            </div>
            <div className="w-8 h-0.5 bg-slate-600"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                <span className="text-purple-300 font-bold text-sm">3</span>
              </div>
              <span className="text-purple-300 text-sm font-medium">Mastery</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VibeModulesPreview;